import { Box, Chip, Divider, Typography, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import ModalDrawer from './ModalDrawer'
import InlineModalPortal from './InlineModal'
import ModalDrawerToolbar from './ModalDrawerToolbar'
import { SetupScreenVM } from '../viewModels/SetupScreenVM'
import SelectableRectButton from './SelectableRectButton'
import { SETUP_TABS } from '../../shared-solo/viewModels/BaseSetupScreenVM'
import Accordion from './Accordion'
import CardContainer from './CardContainer'
import CircleIcon from './CircleIcon'
import icons from '../assets/icons'
import { useVM } from '../lib/hooks/useVM'
import { useNavigate } from 'react-router'
import { Fragment, useEffect } from 'react'
import RectButton from './RectButton'
import TextButton from './TextButton'
import { useI18n } from 'shared-lib/i18n/i18n'
import StyledIcon from './StyledIcon'
import CardButton from './CardButton'

const EmptyCompletedState = () => {
  const { s } = useI18n()
  return (
    <Box sx={{
      py: 6,
      textAlign: 'center',
      backgroundColor: 'background.paper',
      borderRadius: 1,
    }}>
      <Typography variant='body1' color='text.primary' sx={{ fontWeight: 'medium' }}>
        {s('No completed tasks yet')}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        {s('Complete tasks from the Todo tab to see them here')}
      </Typography>
    </Box>
  )
}

const AllTasksCompletedState = () => {
  const { s } = useI18n()
  return (
    <Box sx={{
      py: 6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      textAlign: 'center',
      backgroundColor: 'background.paper',
      borderRadius: 1,
    }}>
      <CircleIcon icon8='check' color='green' size='large' />
      <Typography variant='body1' color='text.primary' sx={{ fontWeight: 'medium' }}>
        {s('All tasks completed')}
      </Typography>
    </Box>
  )
}

const SetupModal = () => {
  const vm = useVM(SetupScreenVM)
  const { setupStore } = vm.store
  const theme = useTheme()
  const { s } = useI18n()
  const navigate = useNavigate()

  useEffect(() => {
    vm.store.navigationStore.setNavigation(navigate)
  }, [vm.store, navigate])

  return (
    <>
      <InlineModalPortal visible={setupStore.isModalOpen} modalKey='setup-modal'>
        <ModalDrawer
          onClose={vm.closeModal}
          size='small'
          scrollable
          blocker={null}
          modalKey='setup-modal'
        >
          <ModalDrawerToolbar title={!setupStore.gettingStartedFirstStepsComplete ? s('Getting started') : s('Setup')} />
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              {SETUP_TABS.map(tab => (
                <SelectableRectButton
                  key={tab.id}
                  selected={vm.selectedTab === tab.id}
                  onClick={() => vm.setSelectedTab(tab.id)}
                  variant='outlined'
                  size='medium'
                  style={{ flex: 1 }}
                >
                  {tab.label}
                </SelectableRectButton>
              ))}
            </Box>

            {vm.selectedTab === 'todo' && (
              !setupStore.gettingStartedFirstStepsComplete ? (
                <CardContainer>
                  {setupStore.remainingGettingStartedSteps.map((step, index) => (
                    <Fragment key={step.id}>
                      <CardButton
                        text={step.title}
                        onClick={() => vm.getStepOnPress(step.id)?.()}
                        leftComponent={<StyledIcon name={step.icon as keyof typeof icons} color='darkGray' size={28} />}
                        rightIcon='arrow'
                      />
                      {index !== setupStore.remainingGettingStartedSteps.length - 1 && <Divider />}
                    </Fragment>
                  ))}
                </CardContainer>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {vm.allTasksCompleted ? (
                    <AllTasksCompletedState />
                  ) : vm.todoGroups.map(group => (
                    <Accordion
                      key={group.id}
                      title={group.name}
                      open={vm.openAccordions[group.id]}
                      onExpand={() => vm.toggleAccordion(group.id)}
                      labelGridWidth='auto'
                      titleEndComponent={
                        <Chip
                          label={group.items.length}
                          sx={{
                            background: theme.palette.secondary.main,
                            color: theme.palette.primary.main,
                            width: theme.spacing(6),
                            height: theme.spacing(6),
                            borderRadius: '50%',
                          }}
                        />
                      }
                    >
                      <CardContainer>
                        {group.items.map((step, index) => (
                          <Fragment key={step.id}>
                            <CardButton
                              text={step.title}
                              onClick={() => vm.getStepOnPress(step.id)?.()}
                              leftComponent={<StyledIcon name={step.icon as keyof typeof icons} color='darkGray' size={28} />}
                              rightIcon='arrow'
                            />
                            {index !== group.items.length - 1 && <Divider />}
                          </Fragment>
                        ))}
                      </CardContainer>
                    </Accordion>
                  ))}
                </Box>
              )
            )}

            {vm.selectedTab === 'completed' && (
              !setupStore.gettingStartedFirstStepsComplete ? (
                setupStore.completedGettingStartedSteps.length === 0 ? (
                  <EmptyCompletedState />
                ) : (
                  <CardContainer>
                    {setupStore.completedGettingStartedSteps.map((step, index) => (
                      <Fragment key={step.id}>
                        <CardButton
                          text={step.title}
                          leftComponent={<CircleIcon icon8='check' color='green' size='small' />}
                          onClick={vm.isDev ? () => vm.resetStep(step.id) : undefined}
                        />
                        {index !== setupStore.completedGettingStartedSteps.length - 1 && <Divider />}
                      </Fragment>
                    ))}
                  </CardContainer>
                )
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {vm.completedGroups.length === 0 ? (
                    <EmptyCompletedState />
                  ) : (
                    vm.completedGroups.map(group => (
                      <Box key={group.id}>
                        <Typography
                          variant='subtitle1'
                          fontWeight='bold'
                          sx={{ mb: 1.5 }}
                        >
                          {group.name}
                        </Typography>
                        <CardContainer>
                          {group.items.map((step, index) => (
                            <Fragment key={step.id}>
                              <CardButton
                                text={step.title}
                                leftComponent={<CircleIcon icon8='check' color='green' size='small' />}
                                onClick={vm.isDev ? () => vm.resetStep(step.id) : undefined}
                                key={step.id}
                              />
                              {index !== group.items.length - 1 && <Divider />}
                            </Fragment>
                          ))}
                        </CardContainer>
                      </Box>
                    ))
                  )}
                </Box>
              )
            )}
          </Box>
        </ModalDrawer>
      </InlineModalPortal >

      <InlineModalPortal visible={!!vm.activeStep} modalKey='setup-step-detail'>
        <ModalDrawer
          onClose={setupStore.closeStepDrawer}
          size='small'
          scrollable
          blocker={null}
          modalKey='setup-step-detail'
        >
          {vm.activeStep && (
            <>
              {/* <ModalDrawerToolbar title={s('Setup')} /> */}
              <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <CardContainer sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  pt: 8,
                  pb: 8,
                  px: 4,
                }}>
                  <StyledIcon
                    name={vm.activeStep.icon as keyof typeof icons}
                    color='darkGray'
                    size={80}
                  />
                  <Typography variant='h5' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {vm.activeStep.title}
                  </Typography>
                  {vm.activeStep.description && (
                    <Typography variant='body1' color='text.secondary' sx={{ textAlign: 'center' }}>
                      {vm.activeStep.description}
                    </Typography>
                  )}
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <RectButton fullWidth size='medium' onClick={vm.handleSetup}>
                      {s('Setup')}
                    </RectButton>
                    {(!vm.isRequiredStep || vm.isDev) && (
                      <TextButton fullWidth onClick={vm.handleSkip}>
                        {vm.isRequiredStep ? s('Skip in dev') : s('Skip setting up this feature')}
                      </TextButton>
                    )}
                  </Box>
                </CardContainer>
              </Box>
            </>
          )}
        </ModalDrawer>
      </InlineModalPortal>
    </>
  )
}

export default observer(SetupModal)
